/**
 * Termos de uso (`/terms`). Texto puro, sem React: a tela e o teste
 * (`scripts/seo-institucional.test.mjs`) leem daqui. A seção do recebimento
 * pelo link fica em `components/legal/payments-terms-section.tsx`, porque tem
 * versão própria ligada ao aceite gravado (`payments.terms_version`).
 *
 * Fatos conferidos em 2026-10-03: plano grátis e recursos pagos em
 * `api/src/plan-access/plan-access.ts` (via `content/facts.ts`); sem
 * assinatura paga, a conta volta ao grátis (`getPlanTier`); exclusão cancela a
 * assinatura do site (`api/src/user/user.service.ts`); assinatura no Android
 * pelo Google Play (`app/subscription/plans.tsx`).
 *
 * Revisão jurídica fica para depois (D7).
 */
import { COMPANY, FREE_PLAN, PROSE } from '../facts';
import { LAW_LINKS, type LegalSection } from './privacy';

export const TERMS_PATH = '/terms';
export const TERMS_TITLE = 'Termos de uso';
export const TERMS_UPDATED_AT = '2026-10-06';
export const TERMS_UPDATED_LABEL = 'Última atualização: 6 de outubro de 2026';
export const TERMS_DESCRIPTION =
  'Termos de uso da Controlapag: conta grátis e planos, assinatura e cancelamento, arrependimento, mensagens aos seus clientes, recebimento pelo link e foro.';

const city = `${COMPANY.city}/${COMPANY.region}`;

/** Seções antes da seção do recebimento pelo link (que entra como a próxima). */
export const TERMS_SECTIONS_BEFORE_PAYMENTS: LegalSection[] = [
  {
    id: 'aceitacao',
    title: '1. Aceitação dos termos',
    paragraphs: [
      'Ao criar uma conta ou usar a Controlapag, você concorda com estes termos de uso e com a política de privacidade. Se não concordar com alguma parte, não use o serviço.',
    ],
    links: [{ label: 'Política de privacidade', url: '/privacidade' }],
  },
  {
    id: 'quem-somos',
    title: '2. Quem somos',
    paragraphs: [
      `A Controlapag é um app brasileiro de agenda, clientes e cobrança para quem presta serviço, com sede em ${city}, Brasil. O contato para qualquer assunto destes termos é o e-mail ${COMPANY.email}, ${COMPANY.supportHours}, com resposta em ${COMPANY.supportResponse}.`,
    ],
    links: [{ label: 'Fale com a gente', url: '/contato' }],
  },
  {
    id: 'servico',
    title: '3. O que é o serviço',
    paragraphs: [
      `A Controlapag ajuda profissionais autônomos e pequenos negócios de serviço a organizar clientes, agenda e cobranças, ${PROSE.platforms}. O cliente do profissional pode ter conta própria, grátis, para ver horários e cobranças e pagar pelo link.`,
    ],
  },
  {
    id: 'conta',
    title: '4. Cadastro e conta',
    paragraphs: [
      'A conta é para maiores de 18 anos. Você informa dados verdadeiros, guarda sua senha e responde pelo que acontece na sua conta. Se perceber um acesso que não foi seu, troque a senha e escreva para o e-mail de contato.',
    ],
  },
  {
    id: 'planos',
    title: '5. Planos e conta grátis',
    paragraphs: [
      `A conta grátis não tem prazo e atende até ${FREE_PLAN.maxClientsPerMonth} clientes por mês, com ${FREE_PLAN.includes.join(', ')}.`,
      `Os planos pagos custam ${PROSE.fromPrice} no site e incluem ${FREE_PLAN.paidOnly.join('; ')}. Os preços e limites de cada plano ficam na tela de planos antes da compra. Nas lojas de aplicativo o preço pode ser diferente e é sempre mostrado pela loja antes de confirmar.`,
      'Não dá para usar os planos pagos sem pagar por conta própria. Um período de uso dos planos pagos sem custo só existe quando a Controlapag libera para uma conta, e o fim dele aparece no app.',
      'Se a assinatura acabar ou não for paga, a conta continua funcionando com o que a conta grátis oferece. Os recursos pagos, como lembretes automáticos e cobranças recorrentes, ficam parados até você assinar de novo.',
    ],
  },
  {
    id: 'assinaturas',
    title: '6. Assinatura, renovação e cancelamento',
    paragraphs: [
      'Os planos pagos são assinaturas mensais com renovação automática. Onde você assinou define como paga e como cancela:',
      'Excluir a conta cancela na hora a assinatura feita no site, mas não cancela uma assinatura da App Store nem do Google Play: cancele também na loja.',
    ],
    items: [
      'No site: pagamento com cartão de crédito, cobrado todo mês até você cancelar. Para cancelar, use Configurações › Seu plano › Cancelar assinatura. O plano continua até o fim do período já pago e não há nova cobrança. Até lá, dá para desfazer o cancelamento no mesmo lugar.',
      'No iPhone e no iPad: compra pela App Store, cobrada na sua conta Apple. A renovação acontece a cada mês, a menos que você cancele até 24 horas antes do fim do período, em Ajustes › seu nome › Assinaturas.',
      'No Android: compra pelo Google Play, cobrada na sua conta Google, todo mês até você cancelar em Google Play › Pagamentos e assinaturas.',
    ],
  },
  {
    id: 'arrependimento',
    title: '7. Arrependimento e reembolso',
    paragraphs: [
      'Na compra feita pela internet, o Código de Defesa do Consumidor (art. 49) dá 7 dias, contados da contratação, para desistir. Se você assinou no site e cancelar nesse prazo em Configurações › Seu plano › Cancelar assinatura, o plano é encerrado na hora e devolvemos todo o valor pago no mesmo cartão. O estorno aparece na fatura conforme o prazo do seu banco, em geral até 10 dias úteis.',
      'Na compra pela App Store ou pelo Google Play, quem cobra e devolve é a loja: o pedido de reembolso é feito nela, pelas regras dela. Se precisar, a Controlapag ajuda com o que estiver ao alcance dela.',
      'Fora desses casos, o mês já pago não é devolvido quando você cancela, e os recursos pagos continuam até o fim do período.',
    ],
    links: [LAW_LINKS.cdc],
  },
  {
    id: 'uso',
    title: '8. Uso aceitável',
    paragraphs: [
      'Você usa a Controlapag só para fins lícitos. É proibido: (a) violar a lei; (b) publicar ou enviar conteúdo ofensivo, difamatório, discriminatório, sexual, violento, de assédio ou que viole direitos de terceiros, em mensagens, fotos, perfis ou descrições de serviço; (c) tentar interferir na segurança do serviço.',
      'Qualquer pessoa pode denunciar uma mensagem ou um perfil e bloquear quem a incomoda. As denúncias são analisadas em até 24 horas, e o conteúdo que violar estes termos é removido, com a suspensão ou exclusão da conta responsável.',
    ],
  },
  {
    id: 'clientes',
    title: '9. Seus clientes e as mensagens enviadas em seu nome',
    paragraphs: [
      'Os dados que você cadastra sobre seus clientes são seus: você é o controlador desses dados, e a Controlapag trata como operadora, só para o serviço funcionar. Cadastre apenas quem é de fato seu cliente e mantenha os dados corretos.',
      'Nos planos pagos, os lembretes de horário e de cobrança saem pelo WhatsApp do número da Controlapag, em seu nome, com o que você configurou. Não use o serviço para propaganda em massa nem para quem não é seu cliente. O cliente que responder SAIR deixa de receber as mensagens automáticas.',
      'Você responde pelo que cobra e pelo valor que combina com o cliente. A Controlapag não presta o seu serviço nem participa do acordo entre você e seu cliente.',
    ],
  },
];

/** Seções depois da seção do recebimento pelo link (que é a 10). */
export const TERMS_SECTIONS_AFTER_PAYMENTS: LegalSection[] = [
  {
    id: 'propriedade',
    title: '11. Propriedade intelectual',
    paragraphs: [
      'O conteúdo, o design, a marca e o software da Controlapag são da Controlapag ou de quem os licenciou, e são protegidos pela lei de direitos autorais e de propriedade intelectual. O que você cadastra continua seu.',
    ],
  },
  {
    id: 'suspensao',
    title: '12. Suspensão e encerramento',
    paragraphs: [
      'Você pode encerrar a conta quando quiser, em Perfil › Excluir conta. O que é apagado e o que a lei manda guardar estão na política de privacidade.',
      'A Controlapag pode suspender ou encerrar uma conta que viole estes termos, que seja usada para fraude ou golpe, ou por ordem de autoridade. Sempre que possível, avisamos antes pelo e-mail da conta e dizemos o motivo.',
    ],
    links: [{ label: 'Excluir sua conta', url: '/excluir-conta' }],
  },
  {
    id: 'responsabilidade',
    title: '13. Limitação de responsabilidade',
    paragraphs: [
      'A Controlapag trabalha para o serviço ficar no ar e funcionar como descrito, mas não responde por danos indiretos de falha de terceiros (como a internet, o WhatsApp ou o banco) nem pelo acordo entre você e seus clientes. Nada aqui limita os direitos que o Código de Defesa do Consumidor garante.',
    ],
  },
  {
    id: 'alteracoes',
    title: '14. Mudanças nestes termos',
    paragraphs: [
      'Quando estes termos mudarem, a data no topo muda junto. Se a mudança for importante, avisamos no app ou por e-mail antes de valer.',
    ],
  },
  {
    id: 'lei',
    title: '15. Lei e foro',
    paragraphs: [
      `Estes termos seguem a lei brasileira. Qualquer disputa é resolvida no foro do domicílio do consumidor, como prevê o Código de Defesa do Consumidor. Antes de ir à Justiça, você pode escrever para o e-mail de contato, que respondemos em ${COMPANY.supportResponse}.`,
    ],
    links: [LAW_LINKS.cdc, LAW_LINKS.ecommerce],
  },
  {
    id: 'contato',
    title: '16. Contato',
    paragraphs: [
      `Dúvidas sobre estes termos, denúncia de conteúdo ou de usuário: escreva para ${COMPANY.email}.`,
    ],
    links: [{ label: 'Central de suporte', url: '/suporte' }],
  },
];

export const TERMS_PAYMENTS_SECTION_NUMBER = TERMS_SECTIONS_BEFORE_PAYMENTS.length + 1;
